import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { MagnifyingGlass, Phone, Envelope, MapPin, SmileyWink, CheckCircle, Flame, ListBullets, Sparkle, PhoneCall } from "@phosphor-icons/react";

const SOURCE_LABELS = {
    "smile-quiz": "Smile Quiz",
    "counsellor-callback": "Quick Callback",
    "contact-form": "Contact form",
};

const ANSWER_FIELDS = [
    { key: "concern", label: "Concern" },
    { key: "severity", label: "Severity" },
    { key: "age", label: "Age" },
    { key: "history_braces", label: "Prior treatment" },
    { key: "timeline", label: "Timeline" },
];

function scoreTier(score) {
    if (score >= 70) return { label: "Hot", cls: "bg-rose-100 text-rose-700 border-rose-200" };
    if (score >= 45) return { label: "Warm", cls: "bg-amber-100 text-amber-700 border-amber-200" };
    if (score > 0) return { label: "Cool", cls: "bg-sky-100 text-sky-700 border-sky-200" };
    return { label: "Cold", cls: "bg-secondary text-muted-foreground border-border" };
}

const SOURCE_TABS = [
    {
        key: "all",
        label: "All leads",
        sublabel: "Every entry point",
        icon: ListBullets,
        color: "hsl(192 100% 31%)",
    },
    {
        key: "smile-quiz",
        label: "Smile Quiz",
        sublabel: "From the assessment",
        icon: Sparkle,
        color: "hsl(280 60% 55%)",
    },
    {
        key: "counsellor-callback",
        label: "Quick Callback",
        sublabel: "Homepage callback form",
        icon: PhoneCall,
        color: "hsl(27 87% 50%)",
    },
];

export default function AdminLeads() {
    const [leads, setLeads] = useState([]);
    const [counsellors, setCounsellors] = useState([]);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [assigning, setAssigning] = useState({}); // {leadId: true}
    const [activeSource, setActiveSource] = useState("all"); // all | smile-quiz | counsellor-callback

    useEffect(() => {
        Promise.all([
            api.get("/admin/leads"),
            api.get("/admin/staff"),
        ]).then(([l, s]) => {
            setLeads(l.data);
            // Active counsellors only, these are who leads get routed to
            setCounsellors(
                (s.data || []).filter((x) => x.is_active !== false && x.staff_type === "counsellor")
            );
        }).finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const counsellorById = useMemo(() => {
        const m = {};
        counsellors.forEach((c) => { m[c.id] = c; });
        return m;
    }, [counsellors]);

    const counts = useMemo(() => ({
        all: leads.length,
        "smile-quiz": leads.filter((l) => l.source === "smile-quiz").length,
        "counsellor-callback": leads.filter((l) => l.source === "counsellor-callback").length,
    }), [leads]);

    const filtered = useMemo(() => {
        let list = leads;
        if (activeSource !== "all") {
            list = list.filter((l) => l.source === activeSource);
        }
        if (q) {
            const low = q.toLowerCase();
            list = list.filter((l) =>
                (l.name || "").toLowerCase().includes(low) ||
                (l.phone || "").includes(q) ||
                (l.city || "").toLowerCase().includes(low) ||
                (l.source || "").toLowerCase().includes(low)
            );
        }
        return list;
    }, [leads, q, activeSource]);

    const handleAssign = async (leadId, staffId) => {
        if (!staffId) return;
        setAssigning({ ...assigning, [leadId]: true });
        try {
            await api.put(`/admin/leads/${leadId}/assign-staff`, { staff_id: staffId });
            setLeads((prev) => prev.map((l) =>
                l.id === leadId ? { ...l, assigned_staff_id: staffId, lead_status: "assigned" } : l
            ));
            toast.success("Lead assigned to Patient Engagement Specialist");
        } catch {
            toast.error("Failed to assign lead");
        } finally {
            setAssigning({ ...assigning, [leadId]: false });
        }
    };

    return (
        <div data-testid="admin-leads-page">
            <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                    <h1 className="font-display text-3xl font-bold">Leads</h1>
                    <p className="text-muted-foreground mt-1 text-sm">{leads.length} total, pick a category below to focus your inbox.</p>
                </div>
                <div className="relative w-full max-w-xs">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        placeholder="Search name, phone, city..."
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="pl-9 h-10"
                        data-testid="leads-search"
                    />
                </div>
            </div>

            {/* Source filter cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8" data-testid="leads-source-filters">
                {SOURCE_TABS.map((tab) => {
                    const isActive = activeSource === tab.key;
                    const count = counts[tab.key] ?? 0;
                    return (
                        <button
                            key={tab.key}
                            type="button"
                            onClick={() => setActiveSource(tab.key)}
                            data-testid={`leads-filter-${tab.key}`}
                            className={`text-left p-4 rounded-2xl border transition-all ${
                                isActive
                                    ? "border-primary bg-primary text-white shadow-md scale-[1.01]"
                                    : "border-border bg-card hover:border-primary/40 hover:bg-secondary/50"
                            }`}
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div
                                    className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                                        isActive ? "bg-white/15" : "bg-secondary"
                                    }`}
                                >
                                    <tab.icon size={20} weight="duotone" color={isActive ? "white" : tab.color} />
                                </div>
                                <div className={`font-display text-3xl font-bold ${isActive ? "text-white" : ""}`}>
                                    {count}
                                </div>
                            </div>
                            <div className={`mt-3 font-semibold text-sm ${isActive ? "text-white" : ""}`}>{tab.label}</div>
                            <div className={`text-xs mt-0.5 ${isActive ? "text-white/70" : "text-muted-foreground"}`}>
                                {tab.sublabel}
                            </div>
                        </button>
                    );
                })}
            </div>

            {loading && <div className="text-muted-foreground">Loading…</div>}

            {!loading && filtered.length === 0 && (
                <div className="bg-card border border-border rounded-2xl p-10 text-center text-muted-foreground">
                    <SmileyWink size={32} className="mx-auto mb-2 opacity-50" />
                    No leads yet.
                </div>
            )}

            <div className="bg-card border border-border rounded-2xl overflow-hidden" data-testid="leads-list">
                {/* Header row (desktop only) */}
                <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 bg-secondary/40 text-[11px] uppercase tracking-wider font-semibold text-muted-foreground border-b border-border">
                    <div className="col-span-3">Lead</div>
                    <div className="col-span-2">Contact</div>
                    <div className="col-span-2">Source</div>
                    <div className="col-span-1">Score</div>
                    <div className="col-span-3">Assigned</div>
                    <div className="col-span-1 text-right">Date</div>
                </div>

                {filtered.map((l) => {
                    const assigned = l.assigned_staff_id ? counsellorById[l.assigned_staff_id] : null;
                    const tier = scoreTier(l.score || 0);
                    const answerSummary = ANSWER_FIELDS
                        .filter((f) => l.answers?.[f.key])
                        .map((f) => String(l.answers[f.key]).replace(/start_|_/g, " ").trim())
                        .join(" · ");
                    return (
                        <div
                            key={l.id}
                            className="grid grid-cols-12 gap-3 px-5 py-4 border-b border-border last:border-b-0 hover:bg-secondary/30 transition-colors items-center"
                            data-testid={`lead-${l.id}`}
                        >
                            {/* Lead: name + city + optional quiz summary */}
                            <div className="col-span-12 md:col-span-3 min-w-0">
                                <div className="font-medium text-sm truncate">{l.name}</div>
                                <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                                    <MapPin size={11} /> {l.city || ","}
                                </div>
                                {answerSummary && (
                                    <div className="text-[11px] text-muted-foreground mt-1 capitalize truncate" title={answerSummary}>
                                        {answerSummary}
                                    </div>
                                )}
                            </div>

                            {/* Contact */}
                            <div className="col-span-12 md:col-span-2 min-w-0">
                                <a
                                    href={`tel:${l.phone}`}
                                    className="text-sm flex items-center gap-1.5 hover:text-primary"
                                    data-testid={`lead-phone-${l.id}`}
                                >
                                    <Phone size={12} className="text-muted-foreground shrink-0" />
                                    <span className="truncate">{l.phone}</span>
                                </a>
                                {l.email && (
                                    <div className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5 truncate">
                                        <Envelope size={11} className="shrink-0" />
                                        <span className="truncate">{l.email}</span>
                                    </div>
                                )}
                            </div>

                            {/* Source pill */}
                            <div className="col-span-6 md:col-span-2">
                                <span className="text-[10px] px-2 py-1 rounded-full bg-secondary text-foreground uppercase font-semibold inline-block">
                                    {SOURCE_LABELS[l.source] || l.source || "Smile Quiz"}
                                </span>
                            </div>

                            {/* Score */}
                            <div className="col-span-6 md:col-span-1">
                                <span
                                    className={`text-[10px] px-2 py-1 rounded-full border uppercase font-semibold inline-flex items-center gap-1 ${tier.cls}`}
                                    data-testid={`lead-score-${l.id}`}
                                    title={`Lead score ${l.score || 0}/100`}
                                >
                                    {tier.label === "Hot" && <Flame size={10} weight="fill" />}
                                    {l.score || 0}
                                </span>
                            </div>

                            {/* Assigned doctor or assign dropdown */}
                            <div className="col-span-12 md:col-span-3 min-w-0">
                                {assigned ? (
                                    <div className="flex items-center gap-1.5 text-xs" data-testid={`lead-assigned-${l.id}`}>
                                        <CheckCircle size={14} weight="fill" className="text-emerald-600 shrink-0" />
                                        <span className="font-medium truncate">{assigned.name}</span>
                                    </div>
                                ) : (
                                    <select
                                        defaultValue=""
                                        disabled={assigning[l.id] || counsellors.length === 0}
                                        onChange={(e) => handleAssign(l.id, e.target.value)}
                                        className="w-full h-8 text-xs rounded-lg border border-border bg-background px-2"
                                        data-testid={`lead-assign-select-${l.id}`}
                                    >
                                        <option value="" disabled>
                                            {counsellors.length === 0 ? "Add a Patient Engagement Specialist first" : "Assign specialist…"}
                                        </option>
                                        {counsellors.map((c) => (
                                            <option key={c.id} value={c.id}>
                                                {c.name}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>

                            {/* Date */}
                            <div className="col-span-12 md:col-span-1 md:text-right">
                                <div className="text-xs text-muted-foreground">
                                    {new Date(l.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
